import {finalize, Observable} from 'rxjs';
import {saveAs} from 'file-saver';
import {FileTransfer} from 'web-angular-client';

import {BlobUtils} from './blob-utils';

/**
 * Inputs the secure-side invoice download flow needs from the calling component.
 * Each callback is invoked at most once per call to {@link downloadInvoice}.
 */
export interface InvoiceDownloadDeps
{
    invoiceLoader(accountId: number): Observable<FileTransfer>;

    onStart(): void;

    onComplete(): void;

    onError(message: string): void;
}

export const INVOICE_DOWNLOAD_FAILURE_MESSAGE =
    'Failed to download the TaxInvoice, please contact support for assistance';

/**
 * Orchestrates the secure-side invoice download: starts the loader, calls
 * {@link InvoiceDownloadDeps.invoiceLoader}, and on success decodes the base64
 * {@code fileContent} via {@link BlobUtils#base64ToPdfBlob} and hands the resulting Blob to
 * {@code saveAs}.
 * Surfaces {@link INVOICE_DOWNLOAD_FAILURE_MESSAGE} via {@link InvoiceDownloadDeps.onError} on
 * either a null {@code fileContent} (broken-contract regression path) or an HTTP error.
 */
export function downloadInvoice(accountId: number, deps: InvoiceDownloadDeps): void
{
    deps.onStart();
    deps.invoiceLoader(accountId)
        .pipe(finalize(() => deps.onComplete()))
        .subscribe({
            next: (fileTransferDTO: FileTransfer) =>
            {
                if (null != fileTransferDTO.fileContent)
                {
                    const blob = BlobUtils.base64ToPdfBlob(fileTransferDTO.fileContent);
                    saveAs(blob, `${fileTransferDTO.fileName}.${fileTransferDTO.fileExtension}`);
                }
                else
                {
                    deps.onError(INVOICE_DOWNLOAD_FAILURE_MESSAGE);
                }
            },
            error: () => deps.onError(INVOICE_DOWNLOAD_FAILURE_MESSAGE)
        });
}
